import {Item, Rental} from "../types";
import {
    Avatar,
    classNames,
    EnglishKeyboardLayout,
    GermanKeyboardLayout,
    IconNames,
    Keyboard,
    layout,
    MaterialIcon,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextInput,
    theme,
    typography,
    useOccupancies,
    useTypeRef,
    View,
    ViewContent
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {Language, Occupancy} from "@variocube/app-api";
import {ItemTableCell} from "./ItemTableCell";
import {findCurrentItemRental} from "../hooks";
import {stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";

export interface ItemTabProps {
    rentals: Rental[];
    items: Item[];
    onOpen: (item: Item) => void;
}

export function ItemTab({items: allItems, rentals, onOpen}: ItemTabProps) {
    const occupancies = useOccupancies();
    const [query, setQuery] = useState("");
    const {t, language} = useLocalization();

    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [hideKeyboardTimeout, setHideKeyboardTimeout] = useState<number>();
    const [type, setType] = useTypeRef();

    const handleQueryFocus = useCallback(() => {
        clearTimeout(hideKeyboardTimeout);
        setKeyboardOpen(true);
    }, [hideKeyboardTimeout]);

    const handleQueryBlur = useCallback(() => {
        setHideKeyboardTimeout(window.setTimeout(() => setKeyboardOpen(false), 250));
    }, []);

    const items = useMemo(() => {
        if (allItems.length > 50 && !query) return null;
        else if (query) {
            const q = query.toLowerCase();
            return allItems.filter(item => {
                return (item.foreignId && item.foreignId.toLowerCase().indexOf(q) >= 0)
                    || (item.name.toLowerCase().indexOf(q) >= 0);
            })
        }
        return allItems;
    }, [allItems, query]);

    return (
        <View>
            <div className={classNames(layout.horizontal, layout.end)}>
                <div className={styles.queryInputContainer}>
                    <div className={styles.searchIconContainer}>
                        <MaterialIcon icon={IconNames.search} className={styles.searchIcon}/>
                    </div>
                    <TextInput
                        value={query}
                        onChange={setQuery}
                        placeholder={t("Search")}
                        typeRef={setType}
                        onFocus={handleQueryFocus}
                        onBlur={handleQueryBlur}
                        outline
                        className={styles.searchInput}
                    />
                </div>
            </div>
            <ViewContent>
                <ItemTable
                    items={items}
                    occupancies={occupancies}
                    rentals={rentals}
                    onOpen={onOpen}
                />
            </ViewContent>
            <div className={classNames(theme.moreContrast, styles.keyboardExpander, keyboardOpen && styles.keyboardExpanderOpen)}>
                <Keyboard
                    className={styles.keyboard}
                    onType={type}
                    layout={language == Language.DE ? GermanKeyboardLayout : EnglishKeyboardLayout}
                />
            </div>
        </View>
    );
}

export interface ItemTableProps {
    items: Item[]|null;
    occupancies: Occupancy[];
    rentals: Rental[];
    onOpen: (item: Item) => void;
}

export function ItemTable({items, occupancies, rentals, onOpen}: ItemTableProps) {
    //const strings = useStrings();
    const {t} = useLocalization();
    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>{t("Item")}</TableCell>
                    <TableCell>{t("Box")}</TableCell>
                    <TableCell>{t("Rental")}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {items === null && (
                    <TableRow>
                        <TableCell colSpan={3}>
                            <div className={typography.alignCenter}>
                                {t("SearchForItems")}
                            </div>
                        </TableCell>
                    </TableRow>
                )}
                {(items && items.length === 0) && (
                    <TableRow>
                        <TableCell colSpan={3}>
                            <div className={typography.alignCenter}>
                                {t("NoItemFound")}
                            </div>
                        </TableCell>
                    </TableRow>
                )}
                {items && items.map(item => {

                    const occupancy = occupancies.find(occupancy => occupancy.content.item == item.uuid);
                    const rental = findCurrentItemRental(rentals, item.uuid);

                    return (
                        <ItemRow
                            key={item.uuid}
                            item={item}
                            boxNumber={occupancy?.boxNumber}
                            rental={rental}
                            onOpen={onOpen}
                        />
                    )
                })}
            </TableBody>
        </Table>
    );
}

export interface ItemRowProps {
    item: Item;
    boxNumber?: string;
    rental?: Rental;
    onOpen: (item: Item) => void;
}

export function ItemRow({item, boxNumber, rental, onOpen}: ItemRowProps) {
    const handleClick = useCallback(() => onOpen(item), [onOpen, item]);
    return (
        <TableRow onClick={handleClick}>
            <ItemTableCell item={item}/>
            <TableCell>
                {boxNumber && (
                    <Avatar size={1.5}>{boxNumber}</Avatar>
                )}
            </TableCell>
            <TableCell>
                {rental?.borrower.name}
            </TableCell>
        </TableRow>
    );
}

const styles = stylesheet({
    queryInputContainer: {
        position: "relative",
        margin: "0.5em 1em 0.5em 0",
        width: "20em",
    },

    searchIconContainer: {
        position: "absolute",
        top: "50%",
        left: "0.5em",
        transform: "translateY(-50%)",
        display: "flex",
    },
    searchIcon: {
        fontSize: "1.5em"
    },
    searchInput: {
        fontSize: "1.5em"
    },

    keyboard: {
        padding: "0.25em 0",
    },
    keyboardExpander: {
        display: "none",
        maxHeight: 0,
        transition: "maxHeight 0.5s",
    },
    keyboardExpanderOpen: {
        display: "block",
        maxHeight: "100%"
    }
})
